import { useCallback, useEffect, useMemo, type ReactNode } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { LocalStorage, useQuery } from "src/framework/commons"
import { getOnlyURLParams } from "src/framework/commons/GetOnlyUrlParams"
import { PermissionUtil } from "src/framework/commons/PermissionUtil"
import { useObservable } from "src/framework/store/storeUtil"
import { padronizaSegmento, padronizaVenda, segmento$, tipoVenda$, venda, type segmento } from "src/store/global.store"
import { AuthMethod } from "./types"
import { doLoginContaUnica, getContaUrlCompleto, isLoggedIn$, loginFromRedirect, permissoes$, validateLogin } from "src/framework/store/LoginStore"

const ULTIMO_SEGMENTO_SELECIONADO_KEY = "solar.ultimo_segmento_selecionado";
const TIPO_VENDA_SELECIONADA_KEY = "solar.tipo_venda_selecionada";
const LOGIN_TOKEN_KEY = "solar.loginToken";

export const AuthLayout = ({ children }: { children: ReactNode }) => {
  const isLoggedIn = useObservable(isLoggedIn$);
  const permissoesObservable = useObservable<string[]>(permissoes$)
  const location = useLocation()
  const query = useQuery();
  const history = useHistory();

  // Campos retornados pelo Conta Intelbras
  const code = query.get('code')
  const session_state = query.get('session_state')
  
  const permissoes = useMemo(()=>{
    return permissoesObservable || []
  },[permissoesObservable])

  const defineAuthMethod = useCallback(async (): Promise<AuthMethod> => {
    const tokenStorage = LocalStorage.get(LOGIN_TOKEN_KEY)
    if (tokenStorage) return AuthMethod.TOKEN_URL

    const token = await validateLogin(tokenStorage)
    if (token) return AuthMethod.USER_LOGGED_IN

    return AuthMethod.CONTA_INTELBRAS;
  }, [])

  const loginFromTokenUrl = useCallback((queryStr: string) => {
    const tokenStorage = LocalStorage.get(LOGIN_TOKEN_KEY)

    loginFromRedirect(tokenStorage).then(errorMessage => {
      if (errorMessage) {
        LocalStorage.remove(LOGIN_TOKEN_KEY)
        return;
      }
    });
  }, [])


  const redirectToContaIntelbras = useCallback(async () => {
    const uri = `${window.location.origin}${window.location.pathname}`
    if (!code && !session_state) {
      const contaUrl = await getContaUrlCompleto(uri)
      if (contaUrl) window.location.href = contaUrl
    }

  }, [code, session_state])

  const loggedUserAction = useCallback(() => {
    // Define o segmento atual
    const segmentoAtual = padronizaSegmento(LocalStorage.get("solar.ultimo_segmento_selecionado") || "on");

    // Define tipo devenda atual
    const vendaAtual = padronizaVenda(LocalStorage.get("solar.tipo_venda_selecionada") || "gerador");

    const permiteOn = PermissionUtil.can(PermissionUtil.PERMISSION_LOJA_ONGRID, permissoes)
    const permiteOff = PermissionUtil.can(PermissionUtil.PERMISSION_LOJA_OFFGRID, permissoes)
    const permiteAvulso = PermissionUtil.can(PermissionUtil.PERMISSION_LOJA_AVULSO, permissoes)
    const permiteKits = (permiteOn && permiteOff);

    if (permiteOn && !permiteOff) {
      segmento$.next('on');
    }

    if (permiteOff && !permiteOn) {
      segmento$.next('off');
    }

    if (permiteOn && permiteOff) {
      segmento$.next(segmentoAtual);
    }

    if (permiteKits && !permiteAvulso) {
      tipoVenda$.next('gerador');
    } else if (!permiteKits && permiteAvulso) {
      tipoVenda$.next('avulso');
    } else if (permiteKits && permiteAvulso) {
      tipoVenda$.next(vendaAtual);
    }
  }, [permissoes])

  const loginFromContaIntelbras = useCallback(() => {
    if (!code || !session_state) return;

    const redirectUrl = `${window.location.origin}${window.location.pathname}`

    const codeVerifier = LocalStorage.get('solar.code_verifier')
    if (codeVerifier) {
      doLoginContaUnica(code, session_state, redirectUrl, codeVerifier).then((error) => {
        if (error) return
        const segmento = LocalStorage.get('solar.ultimo_segmento_selecionado')
        const tipoVenda = LocalStorage.get('solar.tipo_venda_selecionada')
        const newUrl = !segmento ? redirectUrl : `${redirectUrl}?segmento=${segmento}&venda=${tipoVenda}`
        window.location.href = newUrl;
      })
    }
  }, [code, session_state])

  useEffect(()=>{
    // Autenticação via token passado pela URL
    const tokenUrl = query.get("token")

    // Segmento enviado como parâmetro via URL
    const segmentoUrl = query.get("segmento") as segmento;

    // Tipo de Venda enviado como parâmetro via URL
    const tipoVendaUrl = query.get("venda") as venda;

    const allowedParams = [
      { localStorageKey: ULTIMO_SEGMENTO_SELECIONADO_KEY, urlParam: "segmento", value: segmentoUrl },
      { localStorageKey: TIPO_VENDA_SELECIONADA_KEY, urlParam: "venda", value: tipoVendaUrl },
      { localStorageKey: LOGIN_TOKEN_KEY, urlParam: "token", value: tokenUrl },
    ]
    
    if (allowedParams.find(item => !!item.value)) {
      for (const param of allowedParams) {
        if (param.value) {
          LocalStorage.set(param.localStorageKey, param.value);
          query.delete(param.urlParam);
        }
      }
    
      history.replace({
        search: query.toString()
      })
      
      return;
    }

    if (isLoggedIn === false) {
      defineAuthMethod().then(method => {
        switch (method) {
          case AuthMethod.TOKEN_URL:
            const queryStr = getOnlyURLParams(location)
            loginFromTokenUrl(queryStr)
            break;
          case AuthMethod.USER_LOGGED_IN:
            isLoggedIn$.next(true);
            break;
          case AuthMethod.CONTA_INTELBRAS:
            redirectToContaIntelbras();
        }
      })
      return;
    }

    if (isLoggedIn) {
      if (!permissoes.length) {
        // Se já está logado mas não carregou as permissões, não faz nada
        return;
      }

      loggedUserAction();
      return
    }

    if (code && session_state) {
      loginFromContaIntelbras()
    }
  },[code, defineAuthMethod, history, isLoggedIn, location, loggedUserAction, loginFromContaIntelbras, loginFromTokenUrl, permissoes?.length, query, redirectToContaIntelbras, session_state])

  if (!isLoggedIn || !permissoes.length || !segmento$.getValue()) return <></>

  return (
    <>
      {children}
    </>
  )
}
